/**
 * uuid.ts
 *
 * A drop-in `crypto.randomUUID()` that also works on insecure origins.
 *
 * `crypto.randomUUID` is only exposed in secure contexts (HTTPS or
 * `localhost`). On a plain-HTTP LAN origin (e.g. accessing the dev server via
 * an IP like `http://192.168.x.x:3001`) it is `undefined`, so a bare
 * `crypto.randomUUID()` call throws a TypeError and crashes whatever rendered
 * it. This helper returns a v4-shaped UUID either way: the native value when
 * available, otherwise a locally generated one.
 *
 * Usage:
 *   import { randomUUID } from '@/utils/uuid';
 *   const id = randomUUID(); // "1f0c…-…-4…-…-…"
 */

type CryptoLike = {
  randomUUID?: () => string;
  getRandomValues?: <T extends ArrayBufferView | null>(array: T) => T;
};

function getCrypto(): CryptoLike | undefined {
  return (globalThis as { crypto?: CryptoLike }).crypto;
}

// Byte → two-hex-digit lookup table, built once at module load rather than
// rebuilt on every `uuidV4Fallback()` call. `HEX[b]` is the zero-padded hex for
// byte `b` (e.g. 0 → "00", 255 → "ff").
const HEX: string[] = [];
for (let i = 0; i < 256; i++) HEX.push((i + 0x100).toString(16).slice(1));

/**
 * Builds a RFC-4122 version-4 UUID without relying on `crypto.randomUUID`.
 * Prefers `crypto.getRandomValues` for entropy and falls back to `Math.random`
 * on the (rare) environments that expose neither.
 */
export function uuidV4Fallback(): string {
  const bytes = new Uint8Array(16);
  const c = getCrypto();
  if (c?.getRandomValues) {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  // Set the version (4) and variant (10xx) bits per RFC 4122.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  return (
    HEX[bytes[0]] + HEX[bytes[1]] + HEX[bytes[2]] + HEX[bytes[3]] + "-" +
    HEX[bytes[4]] + HEX[bytes[5]] + "-" +
    HEX[bytes[6]] + HEX[bytes[7]] + "-" +
    HEX[bytes[8]] + HEX[bytes[9]] + "-" +
    HEX[bytes[10]] + HEX[bytes[11]] + HEX[bytes[12]] +
    HEX[bytes[13]] + HEX[bytes[14]] + HEX[bytes[15]]
  );
}

/**
 * The native `crypto.randomUUID`, captured ONCE at module load and bound to its
 * `crypto` receiver. Capturing here — before `main.tsx` installs any polyfill —
 * is what makes recursion impossible: if `main.tsx` later overwrites
 * `crypto.randomUUID` with our fallback, this reference still points at the real
 * native implementation (or is `undefined` on insecure origins, where the native
 * one never existed). `randomUUID()` must therefore NEVER re-read
 * `crypto.randomUUID` at call time — doing so could dispatch back into an
 * installed polyfill and blow the stack.
 */
const nativeRandomUUID: (() => string) | undefined = (() => {
  const c = getCrypto();
  return c?.randomUUID ? c.randomUUID.bind(c) : undefined;
})();

/**
 * Returns a v4 UUID string, using the native `crypto.randomUUID` when it was
 * available at module load (secure contexts) and a local fallback otherwise.
 */
export function randomUUID(): string {
  if (nativeRandomUUID) return nativeRandomUUID();
  return uuidV4Fallback();
}
